import { Button } from "@studyplus/boron-ui";
import addMonths from "date-fns/addMonths";
import endOfDay from "date-fns/endOfDay";
import format from "date-fns/format";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import subYears from "date-fns/subYears";
import {
  type FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { DayClickEventHandler, Matcher } from "react-day-picker";
import { createPortal } from "react-dom";
import Icon from "../../../../components/atoms/Icon";
import type { DefaultPeriod } from "../../../../components/features/AiAssistantTemplateForm/validationSchema";
import { StyledDayPicker } from "../../../../components/general/StyledDayPicker";
import styleVars from "../../../../styles/variables.scss";
import {
  convertDefaultPeriodToDateRange,
  type DateRange,
} from "../periodConverter";
import { Controller } from "./Controller";
import { PresetList } from "./PresetList";
import styles from "./styles.scss";

type DraftRange = {
  startDate?: Date;
  endDate?: Date;
};

type Props = {
  dateRange: DateRange;
  onChangeDateRange: (dateRange: DateRange) => void;
};

const formatPreview = (date?: Date) =>
  date ? format(date, "yyyy / MM / dd") : "-- / -- / --";

type PopupPosition = { top: number; right: number };

export const AiAssistantDateRangePicker: FC<Props> = ({
  dateRange,
  onChangeDateRange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRange>({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(
    null,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const numberOfMonths =
    window.innerWidth > parseInt(styleVars.breakpointMobile) ? 2 : 1;

  const openPopup = useCallback(() => {
    setDraft({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
    setIsOpen(true);
  }, [dateRange.startDate, dateRange.endDate]);

  const closePopup = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Controller の位置に基づき popup の固定位置を再計算する
  const updatePopupPosition = useCallback(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const rect = root.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopupPosition(null);
      return;
    }
    updatePopupPosition();
    window.addEventListener("resize", updatePopupPosition);
    // capture 相で listen することで、祖先のスクロールコンテナ（MainLayout の .right など）もまとめて拾う
    window.addEventListener("scroll", updatePopupPosition, true);
    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [isOpen, updatePopupPosition]);

  // popup を portal 化したため insideRef だけでは判定できない。popup 自身の要素も contains 判定に含める。
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }
      if (rootRef.current?.contains(target)) {
        return;
      }
      if (popupRef.current?.contains(target)) {
        return;
      }
      closePopup();
    };
    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [isOpen, closePopup]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      closePopup();
    } else {
      openPopup();
    }
  }, [isOpen, openPopup, closePopup]);

  const handleDayClick = useCallback<DayClickEventHandler>((date) => {
    setDraft((current) => {
      const { startDate, endDate } = current;
      if (startDate && !endDate) {
        if (startDate <= date) {
          return { startDate, endDate: date };
        }
        return { startDate: date, endDate: startDate };
      }
      return { startDate: date, endDate: undefined };
    });
  }, []);

  const handlePresetSelect = useCallback((preset: DefaultPeriod) => {
    const range = convertDefaultPeriodToDateRange(preset, new Date());
    setDraft({ startDate: range.startDate, endDate: range.endDate });
  }, []);

  const handleApply = useCallback(() => {
    if (draft.startDate && draft.endDate) {
      onChangeDateRange({
        startDate: draft.startDate,
        endDate: draft.endDate,
      });
      closePopup();
    }
  }, [draft.startDate, draft.endDate, onChangeDateRange, closePopup]);

  const isApplyDisabled = !draft.startDate || !draft.endDate;

  const disabledMatchers = useMemo<Matcher[]>(() => {
    const today = endOfDay(new Date());
    const oneYearAgo = subYears(today, 1);
    const isOutsideOverallRange = (date: Date) =>
      isAfter(date, today) || isBefore(date, oneYearAgo);

    const isRangeStartOnly = Boolean(draft.startDate && !draft.endDate);
    if (!isRangeStartOnly || !draft.startDate) {
      return [isOutsideOverallRange];
    }

    const startDate = draft.startDate;
    const maxEndDate = addMonths(startDate, 3);
    const isOutsideAllowedRange = (date: Date) =>
      isBefore(date, startDate) || isAfter(date, maxEndDate);

    return [isOutsideOverallRange, isOutsideAllowedRange];
  }, [draft.startDate, draft.endDate]);

  const popupStyle =
    popupPosition !== null
      ? { top: popupPosition.top, right: popupPosition.right }
      : undefined;

  return (
    <div ref={rootRef} className={styles.root}>
      <Controller
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        isOpen={isOpen}
        onClick={handleToggle}
      />
      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            className={styles.popup}
            role="dialog"
            aria-label="データ参照期間のカレンダー"
            style={popupStyle}
          >
            <PresetList onSelect={handlePresetSelect} />
            <div className={styles.mainColumn}>
              <div className={styles.calendar}>
                <StyledDayPicker
                  mode="range"
                  selected={{ from: draft.startDate, to: draft.endDate }}
                  onDayClick={handleDayClick}
                  numberOfMonths={numberOfMonths}
                  defaultMonth={draft.startDate ?? new Date()}
                  disabled={disabledMatchers}
                />
              </div>
              <div className={styles.popupFooter}>
                <div className={styles.preview}>
                  <span className={styles.previewDate}>
                    {formatPreview(draft.startDate)}
                  </span>
                  <Icon
                    name="icon-arrow-open"
                    className={styles.previewArrow}
                  />
                  <span className={styles.previewDate}>
                    {formatPreview(draft.endDate)}
                  </span>
                </div>
                <div className={styles.footerActions}>
                  <Button variant="outline" type="button" onClick={closePopup}>
                    キャンセル
                  </Button>
                  <Button
                    variant="filled"
                    type="button"
                    onClick={handleApply}
                    disabled={isApplyDisabled}
                  >
                    更新
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
